import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal.tsx';

interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmFn>(async () => false);

/**
 * Provides an imperative, promise-returning confirm dialog:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Revoke key?', danger: true })) revoke();
 * Replaces window.confirm() with an on-theme, animated, accessible modal.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOpts(null);
  }, []);

  return (
    <Ctx.Provider value={confirm}>
      {children}
      <Modal
        open={!!opts}
        onClose={() => settle(false)}
        title={
          <span className="row" style={{ gap: 8 }}>
            {opts?.danger && <AlertTriangle size={18} className="err" />}
            {opts?.title}
          </span>
        }
        footer={
          <>
            <button className="ghost" onClick={() => settle(false)}>
              {opts?.cancelLabel ?? 'Cancel'}
            </button>
            <button className={opts?.danger ? 'danger' : ''} onClick={() => settle(true)}>
              {opts?.confirmLabel ?? 'Confirm'}
            </button>
          </>
        }
      >
        {opts?.body && <div className="muted">{opts.body}</div>}
      </Modal>
    </Ctx.Provider>
  );
}

export function useConfirm() {
  return useContext(Ctx);
}
